/**
 * The kitchen as a file somebody else's software can open.
 *
 * Four formats, one pipeline. Everything hard about this is in `centred()`, which is the
 * five lines the AR export was already doing and which every format needs: take the
 * CABINETS -- not the room, which is the room they are standing in -- put their middle at
 * the origin and their floor at y=0, so the model arrives where it is pointed rather than
 * four metres to the left and half underground.
 *
 * THE UNIT TRAP, and it is silent. three.js works in metres (build.js MM = 0.001). glTF and
 * USDZ both DECLARE their unit as metres in the file, so they arrive correctly scaled
 * anywhere. OBJ and STL declare nothing at all, and every CAD package a kitchen maker owns
 * assumes millimetres -- so those two are exported at a thousand times size, which is how
 * they come out at true size at the other end. Get this backwards and the kitchen imports
 * as a four-millimetre model that looks like a rounding error.
 *
 * Exporters are imported lazily, one at a time. They are a few hundred kilobytes each and
 * most people never press any of these buttons.
 */
import * as THREE from 'three'

/**
 * What can come out, in the order it is offered.
 *
 * `scale` is the multiplier applied before export, and it is the whole unit story above:
 * 1 for the two formats that carry their own unit, 1000 for the two that do not.
 */
export const FORMATS = [
  { id: 'glb', name: 'glTF', ext: 'glb', type: 'model/gltf-binary', scale: 1,
    note: 'Colours and materials. Blender, Unreal, SketchUp, most viewers.' },
  { id: 'obj', name: 'OBJ', ext: 'obj', type: 'text/plain', scale: 1000,
    note: 'Shapes without colours. Opens in anything, including old software.' },
  { id: 'stl', name: 'STL', ext: 'stl', type: 'model/stl', scale: 1000,
    note: 'One solid. For 3D printing, and for CAD that wants no materials.' },
  { id: 'usdz', name: 'USDZ', ext: 'usdz', type: 'model/vnd.usdz+zip', scale: 1,
    note: 'Apple AR Quick Look, and the format the In-my-room button uses.' },
]

export const formatOf = (id) => FORMATS.find((f) => f.id === id) ?? null

/**
 * The cabinets alone, centred in x and z with their floor at y=0.
 *
 * A clone, so the scene on screen is never touched: an exporter that traversed the live
 * graph would leave the kitchen moved once the export finished.
 *
 * @param {{ cabinets: THREE.Object3D }} kitchen  the scene, or anything with a `cabinets` group
 * @param {{ scale?: number }} opts
 * @returns {THREE.Group} a root ready to hand to an exporter
 */
export function centred(kitchen, { scale = 1 } = {}) {
  const g = kitchen.cabinets.clone()
  const box = new THREE.Box3().setFromObject(g)
  if (!box.isEmpty()) {
    g.position.set(-(box.min.x + box.max.x) / 2, -box.min.y, -(box.min.z + box.max.z) / 2)
  }
  const root = new THREE.Group()
  root.add(g)
  // Scale on the ROOT, after the child has been positioned, so the centring above is in
  // model units and the scale multiplies the finished thing. Scaling first and centring
  // second gives the same answer; doing half of each does not.
  if (scale !== 1) root.scale.setScalar(scale)
  root.updateMatrixWorld(true)
  return root
}

/**
 * One file, ready to save or share.
 *
 * @param {object} kitchen  the scene
 * @param {string} id       a FORMATS id
 * @param {{ name?: string }} opts
 * @returns {Promise<{ blob: Blob, filename: string, type: string, format: object }>}
 */
export async function exportModel(kitchen, id, opts = {}) {
  const format = formatOf(id)
  if (!format) throw new Error(`no such model format: ${id}`)
  const root = centred(kitchen, { scale: format.scale })
  const data = await encode(root, format.id)
  const blob = data instanceof Blob ? data : new Blob([data], { type: format.type })
  const stem = (opts.name || 'kitchen').replace(/[^\w]+/g, '-').toLowerCase() || 'kitchen'
  return { blob, filename: `${stem}.${format.ext}`, type: format.type, format }
}

async function encode(root, id) {
  if (id === 'usdz') {
    const { USDZExporter } = await import('three/examples/jsm/exporters/USDZExporter.js')
    return new USDZExporter().parseAsync(root, { quickLookCompatible: true })
  }
  if (id === 'glb') {
    const { GLTFExporter } = await import('three/examples/jsm/exporters/GLTFExporter.js')
    // `binary` gives one .glb rather than a .gltf plus a folder of buffers, which is the
    // only shape of glTF that survives being sent to somebody in a message.
    return new Promise((resolve, reject) => {
      new GLTFExporter().parse(root, resolve, reject, { binary: true })
    }).then((buf) => new Blob([buf], { type: 'model/gltf-binary' }))
  }
  if (id === 'obj') {
    const { OBJExporter } = await import('three/examples/jsm/exporters/OBJExporter.js')
    return new OBJExporter().parse(root)
  }
  if (id === 'stl') {
    const { STLExporter } = await import('three/examples/jsm/exporters/STLExporter.js')
    // ASCII rather than binary: a kitchen is a few thousand triangles, the file is still
    // small, and a text STL is one a person can open and check.
    return new STLExporter().parse(root)
  }
  throw new Error(`no encoder for ${id}`)
}
